import { ErrorResponse, type User } from "oidc-client-ts";
import type { PublicRuntimeConfig } from "@/lib/config/publicRuntimeConfig";
import { getCognitoUserManager } from "./cognitoOidcSettings";

/**
 * Current access token, readable synchronously. The OpenAPI client's `pre`
 * middleware cannot await, so the token is mirrored here from UserManager events.
 */
let currentAccessToken: string | null = null;
let listenersAttachedFor: PublicRuntimeConfig | null = null;

export const getConsoleAccessToken = (): string | null => currentAccessToken;

export const setConsoleAccessTokenFromUser = (user: User | null): void => {
  currentAccessToken = user?.access_token ?? null;
};

export const clearConsoleAccessToken = (): void => {
  currentAccessToken = null;
};

/** Keeps {@link getConsoleAccessToken} in step with silent renew and sign-out. */
const attachConsoleAccessTokenListeners = (
  config: PublicRuntimeConfig,
): void => {
  if (listenersAttachedFor === config) {
    return;
  }
  listenersAttachedFor = config;
  const { events } = getCognitoUserManager(config);
  events.addUserLoaded(setConsoleAccessTokenFromUser);
  events.addUserUnloaded(clearConsoleAccessToken);
};

/** Returns a non-expired user, renewing with the refresh token when needed. */
export const loadValidCognitoUser = async (
  config: PublicRuntimeConfig,
): Promise<User | null> => {
  const manager = getCognitoUserManager(config);
  attachConsoleAccessTokenListeners(config);
  let user = await manager.getUser();
  if (user != null && user.expired === true) {
    try {
      user = await manager.signinSilent();
    } catch (e) {
      console.warn("Silent renew failed; user must sign in again", e);
      await manager.removeUser();
      user = null;
    }
  }
  setConsoleAccessTokenFromUser(user);
  return user;
};

/** True when there is no stored user or its access token has passed `exp`. */
export const isConsoleAccessTokenExpired = async (
  config: PublicRuntimeConfig,
): Promise<boolean> => {
  const user = await getCognitoUserManager(config).getUser();
  return user == null || user.expired !== false;
};

export const RENEW_OUTCOME = {
  RENEWED: "renewed",
  REJECTED: "rejected",
  UNAVAILABLE: "unavailable",
} as const;

export type RenewOutcome = (typeof RENEW_OUTCOME)[keyof typeof RENEW_OUTCOME];

let renewInFlight: Promise<RenewOutcome> | null = null;

const renewWithRefreshToken = async (
  config: PublicRuntimeConfig,
): Promise<RenewOutcome> => {
  const manager = getCognitoUserManager(config);
  try {
    const current = await manager.getUser();
    if (current?.refresh_token == null) {
      return RENEW_OUTCOME.REJECTED;
    }
    const user = await manager.signinSilent();
    setConsoleAccessTokenFromUser(user);
    return user == null ? RENEW_OUTCOME.REJECTED : RENEW_OUTCOME.RENEWED;
  } catch (e) {
    // Cognito answered and said no (e.g. invalid_grant): the session is over.
    // Anything else (offline, timeout) says nothing about the session.
    if (e instanceof ErrorResponse) {
      return RENEW_OUTCOME.REJECTED;
    }
    console.warn("Access token renew unavailable", e);
    return RENEW_OUTCOME.UNAVAILABLE;
  }
};

/**
 * Renews the access token with the refresh token. Concurrent callers share one
 * grant, so refresh-token rotation never sees the same token twice.
 */
export const renewConsoleAccessToken = (
  config: PublicRuntimeConfig,
): Promise<RenewOutcome> => {
  renewInFlight ??= renewWithRefreshToken(config).finally(() => {
    renewInFlight = null;
  });
  return renewInFlight;
};

/** Test helper. */
export const resetConsoleAccessTokenForTests = (): void => {
  currentAccessToken = null;
  listenersAttachedFor = null;
  renewInFlight = null;
};
